import type { PackageType, ShiftType } from '@streep/types'

/**
 * Tariefresolutie — pure functies voor business rules 30–42.
 *
 * Regels:
 *  - 30 Pakketten BASIS / SERVICE / ONTZORG / FLEX hebben elk een standaard-uurtarief.
 *  - 31–34 Pakkettarieven per 2026 (Leander Techniek prijslijst).
 *  - 35 Standaard KM-tarief €0,30 (zit in invoice-calculations).
 *  - 36 Klant-override (uurtarief / reistarief / ploegentoeslag) wint van pakket-default.
 *  - 37 Onderhoud: vast uurtarief €62,50.
 *  - 38 Project-rollen (PM, software-engineer, projectmonteur) hebben eigen vaste uurtarieven.
 *  - 39–42 Overuren-toeslagen op basis van post:
 *      Ma-vr 0-2u  → +25% factor
 *      Ma-vr 2-4u  → +50% factor
 *      Zaterdag    → +50% factor
 *      Zon-/feestdag → +100% factor
 */

export const PACKAGE_RATES: Record<PackageType, number> = {
  BASIS: 65.0,
  SERVICE: 68.25,
  ONTZORG: 74.75,
  FLEX: 57.2,
}

export const PACKAGE_LABELS: Record<PackageType, string> = {
  BASIS: 'Pakket 1 - Basis',
  SERVICE: 'Pakket 2 - Service',
  ONTZORG: 'Pakket 3 - Ontzorg',
  FLEX: 'Pakket 4 - Flex',
}

/** Vast onderhoudstarief per uur (excl. BTW). */
export const ONDERHOUD_RATE = 62.5

export type ProjectRole = 'PROJECTMANAGER' | 'SOFTWARE_ENGINEER' | 'PROJECTMONTEUR'

export const PROJECT_ROLE_RATES: Record<ProjectRole, number> = {
  PROJECTMANAGER: 100.0,
  SOFTWARE_ENGINEER: 85.0,
  PROJECTMONTEUR: 72.5,
}

export const PROJECT_ROLE_LABELS: Record<ProjectRole, string> = {
  PROJECTMANAGER: 'Projectmanager',
  SOFTWARE_ENGINEER: 'Software engineer',
  PROJECTMONTEUR: 'Projectmonteur',
}

export type OvertimePost =
  | 'WEEKDAY_0_2'
  | 'WEEKDAY_2_4'
  | 'SATURDAY'
  | 'SUNDAY_HOLIDAY'

export const OVERTIME_FACTORS: Record<OvertimePost, number> = {
  WEEKDAY_0_2: 0.25,
  WEEKDAY_2_4: 0.5,
  SATURDAY: 0.5,
  SUNDAY_HOLIDAY: 1.0,
}

export const OVERTIME_LABELS: Record<OvertimePost, string> = {
  WEEKDAY_0_2: 'Ma-vr overwerk 0-2 uur',
  WEEKDAY_2_4: 'Ma-vr overwerk 2-4 uur',
  SATURDAY: 'Zaterdag uren',
  SUNDAY_HOLIDAY: 'Zondag/feestdagen',
}

const isFiniteNumber = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n)

const round2 = (n: number): number => Math.round(n * 100) / 100

export interface ClientTariffResolveInput {
  packageType: PackageType | null
  /** Klant-specifiek override-uurtarief — wint van pakket-default. */
  hourlyRate: number | null
}

/**
 * Effectief uurtarief voor een klant — regels 30–34 + 36.
 *  1. Klant-override uurtarief (indien > 0)
 *  2. Pakket-default
 *  3. BASIS (65,00) als ultieme fallback
 */
export function effectiefUurtarief(input: ClientTariffResolveInput | null): number {
  if (input && isFiniteNumber(input.hourlyRate) && input.hourlyRate > 0) {
    return round2(input.hourlyRate)
  }
  if (input && input.packageType) return PACKAGE_RATES[input.packageType]
  return PACKAGE_RATES.BASIS
}

/**
 * Effectief overuren-uurtarief op basis van basistarief × (1 + factor).
 * Regels 39–42.
 */
export function berekenOverwerkUurtarief(
  basisUurtarief: number,
  post: OvertimePost,
): number {
  if (!isFiniteNumber(basisUurtarief) || basisUurtarief <= 0) return 0
  const factor = OVERTIME_FACTORS[post]
  return round2(basisUurtarief * (1 + factor))
}

/**
 * Effectief overuren-bedrag voor N uren op een specifieke post.
 */
export function berekenOverwerkBedrag(
  uren: number,
  basisUurtarief: number,
  post: OvertimePost,
): number {
  if (!isFiniteNumber(uren) || uren <= 0) return 0
  return round2(uren * berekenOverwerkUurtarief(basisUurtarief, post))
}

/**
 * Tariefcategorie + key voor de globale `Tariff`-tabel. Wordt door de
 * instellingen-pagina gebruikt om defaults te tonen en (admin) bij te werken.
 */
export const TARIFF_CATEGORIES = {
  PAKKET: 'pakket',
  PLOEG: 'ploeg',
  OVERWERK: 'overwerk',
  PROJECT: 'project',
  REIS: 'reis',
  BTW: 'btw',
  ONDERHOUD: 'onderhoud',
  // FO-389 (batch-46) — consignatie/standby-VERLONINGStarief (los van het
  // klant-tarief op het rooster; FO-324-scheiding). Keys STANDBY_WEEK/STANDBY_NACHT.
  CONSIGNATIE: 'consignatie',
} as const

export type TariffCategory = (typeof TARIFF_CATEGORIES)[keyof typeof TARIFF_CATEGORIES]

export interface TariffSeedRow {
  category: TariffCategory
  key: string
  label: string
  value: number
  unit: string | null
  sortOrder: number
}

/**
 * Volledige set defaults — gebruikt door seed.ts en als fallback wanneer de
 * `Tariff`-tabel leeg is. Staat hier als single source of truth.
 */
export const TARIFF_SEED: TariffSeedRow[] = [
  // Pakketten
  { category: 'pakket', key: 'BASIS', label: PACKAGE_LABELS.BASIS, value: PACKAGE_RATES.BASIS, unit: 'EUR/u', sortOrder: 1 },
  { category: 'pakket', key: 'SERVICE', label: PACKAGE_LABELS.SERVICE, value: PACKAGE_RATES.SERVICE, unit: 'EUR/u', sortOrder: 2 },
  { category: 'pakket', key: 'ONTZORG', label: PACKAGE_LABELS.ONTZORG, value: PACKAGE_RATES.ONTZORG, unit: 'EUR/u', sortOrder: 3 },
  { category: 'pakket', key: 'FLEX', label: PACKAGE_LABELS.FLEX, value: PACKAGE_RATES.FLEX, unit: 'EUR/u', sortOrder: 4 },
  // Ploegentoeslagen (CAO Metaal & Techniek)
  { category: 'ploeg', key: 'DAG', label: 'Geen ploegen', value: 0, unit: '%', sortOrder: 1 },
  { category: 'ploeg', key: 'PLOEGEN_2', label: '2-ploegendienst', value: 18, unit: '%', sortOrder: 2 },
  { category: 'ploeg', key: 'PLOEGEN_3', label: '3-ploegendienst', value: 26, unit: '%', sortOrder: 3 },
  // PR-G (spec 02 §6.2.A) — PLOEGEN_4 30%, additief tussen 3 en 5.
  { category: 'ploeg', key: 'PLOEGEN_4', label: '4-ploegendienst', value: 30, unit: '%', sortOrder: 4 },
  { category: 'ploeg', key: 'PLOEGEN_5', label: '5-ploegendienst', value: 33, unit: '%', sortOrder: 5 },
  // Overwerk-factors
  { category: 'overwerk', key: 'WEEKDAY_0_2', label: OVERTIME_LABELS.WEEKDAY_0_2, value: 25, unit: '%', sortOrder: 1 },
  { category: 'overwerk', key: 'WEEKDAY_2_4', label: OVERTIME_LABELS.WEEKDAY_2_4, value: 50, unit: '%', sortOrder: 2 },
  { category: 'overwerk', key: 'SATURDAY', label: OVERTIME_LABELS.SATURDAY, value: 50, unit: '%', sortOrder: 3 },
  { category: 'overwerk', key: 'SUNDAY_HOLIDAY', label: OVERTIME_LABELS.SUNDAY_HOLIDAY, value: 100, unit: '%', sortOrder: 4 },
  // Project-rollen
  { category: 'project', key: 'PROJECTMANAGER', label: PROJECT_ROLE_LABELS.PROJECTMANAGER, value: PROJECT_ROLE_RATES.PROJECTMANAGER, unit: 'EUR/u', sortOrder: 1 },
  { category: 'project', key: 'SOFTWARE_ENGINEER', label: PROJECT_ROLE_LABELS.SOFTWARE_ENGINEER, value: PROJECT_ROLE_RATES.SOFTWARE_ENGINEER, unit: 'EUR/u', sortOrder: 2 },
  { category: 'project', key: 'PROJECTMONTEUR', label: PROJECT_ROLE_LABELS.PROJECTMONTEUR, value: PROJECT_ROLE_RATES.PROJECTMONTEUR, unit: 'EUR/u', sortOrder: 3 },
  // Reis + BTW + onderhoud
  { category: 'reis', key: 'KM_TARIEF', label: 'Kilometervergoeding', value: 0.3, unit: 'EUR/km', sortOrder: 1 },
  { category: 'btw', key: 'STANDAARD', label: 'Standaard BTW-percentage', value: 21, unit: '%', sortOrder: 1 },
  { category: 'onderhoud', key: 'UURTARIEF', label: 'Onderhoud uurtarief', value: ONDERHOUD_RATE, unit: 'EUR/u', sortOrder: 1 },
  // FO-389 (batch-46) — consignatie/standby-VERLONINGStarief. value 0 = nog niet
  // ingesteld (geen waarde-gok): de rij bestaat zodat ADMIN 'm in het tarief-beheer
  // invult, maar telt als "ontbreekt" tot er een positief bedrag staat (dan blokkeert
  // genereren met piketweken). De seed overschrijft een bestaande waarde nooit.
  // `STANDBY_WEEK` is de v1-eenheid (per piketweek). `STANDBY_NACHT` is GERESERVEERD
  // voor de v2 per-nacht-variant (werkelijke nachten tellen) en wordt in v1 niet gebruikt.
  { category: 'consignatie', key: 'STANDBY_WEEK',  label: 'Consignatievergoeding per week',  value: 0, unit: 'EUR/week',  sortOrder: 1 },
  { category: 'consignatie', key: 'STANDBY_NACHT', label: 'Consignatievergoeding per nacht (v2, nog niet in gebruik)', value: 0, unit: 'EUR/nacht', sortOrder: 2 },
]

/** Hint of een gegeven (category, key) een bekende combinatie is. */
export function isKnownTariff(category: string, key: string): boolean {
  return TARIFF_SEED.some((r) => r.category === category && r.key === key)
}

// Re-export voor consumenten die ploeg-vertaling nodig hebben.
export type { ShiftType }
